/**
 * Client-side Notification model with attached flows for read state management.
 */

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';

import { NotificationModelSpec } from '@elara/domain/models/NotificationModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';

export function createClientNotificationModel(flows: AppFlowsContainer, firestore: FirestoreContainer) {
  const model = createClientModel(
    firestore,
    NotificationModelSpec,
    () => ({
      /** Format the notification's creation time as a relative string */
      get timeAgo(): string {
        const created = this.createdAt;
        if (!created) return '';
        const date = created instanceof Date ? created : new Date(created);
        const now = Date.now();
        const diff = now - date.getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
      },
    }),
    {
      markRead: flows.markNotificationRead,
      markAllRead: flows.markAllNotificationsRead,
    },
  );
  return model;
}

export type NotificationModel = ReturnType<typeof createClientNotificationModel>;
export interface Notification extends ClientInstanceOf<NotificationModel> {}
